import { cn } from '../lib/cn'

export type DpeGrade = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G'

/** Canonical A→G scale order. Single source of truth for the DPE grade list. */
export const DPE_GRADES = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const satisfies readonly DpeGrade[]

interface DpeGradeBadgeProps {
  grade: DpeGrade
  kwh?: number | null
  size?: 'sm' | 'md' | 'lg'
  variant?: 'square' | 'pill'
  /** Color scale: energy green→red (default) or GES violet. */
  scale?: 'energy' | 'ges'
  className?: string
}

const sizeClasses: Record<NonNullable<DpeGradeBadgeProps['size']>, string> = {
  sm: 'h-5 w-5 text-[10px]',
  md: 'h-7 w-7 text-xs',
  lg: 'h-9 w-9 text-sm',
}

const gradeColors: Record<DpeGrade, string> = {
  A: 'bg-dpe-a',
  B: 'bg-dpe-b',
  C: 'bg-dpe-c',
  D: 'bg-dpe-d',
  E: 'bg-dpe-e',
  F: 'bg-dpe-f',
  G: 'bg-dpe-g',
}

// Official DPE étiquette: black text on A–F, white only on G (red).
const gradeText: Record<DpeGrade, string> = {
  A: 'text-dpe-fg',
  B: 'text-dpe-fg',
  C: 'text-dpe-fg',
  D: 'text-dpe-fg',
  E: 'text-dpe-fg',
  F: 'text-dpe-fg',
  G: 'text-dpe-fg-dark',
}

// GES (émissions) — violet ramp, distinct from the energy scale.
const gesColors: Record<DpeGrade, string> = {
  A: 'bg-ges-a',
  B: 'bg-ges-b',
  C: 'bg-ges-c',
  D: 'bg-ges-d',
  E: 'bg-ges-e',
  F: 'bg-ges-f',
  G: 'bg-ges-g',
}
const gesText: Record<DpeGrade, string> = {
  A: 'text-dpe-fg',
  B: 'text-dpe-fg',
  C: 'text-dpe-fg',
  D: 'text-dpe-fg-dark',
  E: 'text-dpe-fg-dark',
  F: 'text-dpe-fg-dark',
  G: 'text-dpe-fg-dark',
}

export function DpeGradeBadge({
  grade,
  kwh,
  size = 'md',
  variant = 'square',
  scale = 'energy',
  className,
}: DpeGradeBadgeProps) {
  const colors = scale === 'ges' ? gesColors : gradeColors
  const text = scale === 'ges' ? gesText : gradeText
  if (variant === 'pill') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-1 py-0.5 text-[11px] font-semibold',
          colors[grade],
          text[grade],
          className,
        )}
        aria-label={`DPE classe ${grade}`}
      >
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-foreground text-background text-[10px] font-bold">
          {grade}
        </span>
        {kwh != null ? <span className="pr-1.5 font-mono tabular-nums">{kwh} kWh</span> : null}
      </span>
    )
  }
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-md font-bold',
        sizeClasses[size],
        colors[grade],
        text[grade],
        className,
      )}
      aria-label={`DPE classe ${grade}`}
    >
      {/* text-box trims the cap/baseline whitespace so the letter centers on its ink, not its line box */}
      <span className="[text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">{grade}</span>
    </span>
  )
}
